import { useEffect, useRef, useState } from 'react';
import {
  Box, Button, Checkbox, Flex, FormControl, FormLabel, Heading,
  Switch, Text, VStack, Divider, Badge, Spinner, useToast,
  Select, Tag, TagLabel, TagCloseButton, Wrap, WrapItem, Image,
  IconButton,
} from '@chakra-ui/react';
import { MdEdit, MdAdd, MdUpload, MdClose, MdPhoneAndroid, MdPhoneIphone, MdOpenInNew } from 'react-icons/md';
import Page from '../components/common/Page';
import StrongInput from '../components/common/StrongInput';
import StrongTextarea from '../components/common/StrongTextarea';
import useApi from '../lib/hooks/useApi';
import { ApiCommand } from '../lib/Api';
import urlConstants from '../lib/constants/url.constants';

type AppVersionConfig = {
  _id?: string;
  title: string;
  message: string;
  version: string;
  forceUpdate: boolean;
  showPopup: boolean;
  targetVersions: string[];
  publishedVersions: string[];
  imageUrl?: string;
  iosVersion?: string;
  iosStoreUrl?: string;
  androidVersion?: string;
  androidStoreUrl?: string;
  updatedAt?: string;
};

const formatDate = (value?: string) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
};

const NewVersion = () => {
  const toast = useToast();
  const [config, setConfig] = useState<AppVersionConfig | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState<AppVersionConfig>({
    title: '', message: '', version: '', forceUpdate: false, showPopup: false, targetVersions: [], publishedVersions: [], iosVersion: '', iosStoreUrl: '', androidVersion: '', androidStoreUrl: '',
  });

  // Image upload
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageRemoved, setImageRemoved] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Versions manually managed in CMS
  const [newPublishedVersion, setNewPublishedVersion] = useState('');
  const [selectedDropdown, setSelectedDropdown] = useState('');

  const { loading: isFetching, sendRequest: fetchConfig } = useApi<AppVersionConfig>();
  const { loading: isSaving, sendRequest: saveConfig } = useApi<any>();
  const { loading: isTogglingPopup, sendRequest: togglePopupRequest } = useApi<any>();

  const load = () => {
    fetchConfig({
      command: ApiCommand.GET,
      url: urlConstants.appVersion.get,
      callback: (data, error) => {
        if (data) {
          const publishedVersions = data.publishedVersions || [];
          setConfig(data);
          setForm({
            ...data,
            targetVersions: data.targetVersions || [],
            publishedVersions,
          });
        }
        if (error) toast({ title: 'Failed to load', description: error, status: 'error' });
      },
    });
  };

  useEffect(() => { load(); }, []);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setImageRemoved(false);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setImageRemoved(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSave = () => {
    const safeShowPopup = form.showPopup && form.targetVersions.length > 0;
    const payload = new FormData();
    payload.append('title', form.title);
    payload.append('message', form.message);
    payload.append('version', form.version);
    payload.append('forceUpdate', String(form.forceUpdate));
    payload.append('showPopup', String(safeShowPopup));
    payload.append('targetVersions', JSON.stringify(form.targetVersions));
    payload.append('publishedVersions', JSON.stringify(form.publishedVersions));
    payload.append('iosVersion', form.iosVersion ?? '');
    payload.append('iosStoreUrl', form.iosStoreUrl ?? '');
    payload.append('androidVersion', form.androidVersion ?? '');
    payload.append('androidStoreUrl', form.androidStoreUrl ?? '');
    payload.append('removeImage', String(imageRemoved));
    if (imageFile) payload.append('file', imageFile);

    saveConfig({
      command: ApiCommand.PUT,
      url: urlConstants.appVersion.update,
      options: payload,
      callback: (data, error) => {
        if (error) {
          toast({ title: 'Failed to save', description: error, status: 'error' });
          return;
        }
        toast({ title: 'Saved', description: 'Version config updated', status: 'success' });
        const nextConfig = data?.config ?? form;
        setConfig(nextConfig);
        setForm({
          ...nextConfig,
          targetVersions: nextConfig.targetVersions || [],
          publishedVersions: nextConfig.publishedVersions || [],
        });
        setImageFile(null);
        setImagePreview(null);
        setImageRemoved(false);
        setIsEditing(false);
      },
    });
  };

  const handleCancel = () => {
    if (config) {
      setForm({
        ...config,
        targetVersions: config.targetVersions || [],
        publishedVersions: config.publishedVersions || [],
      });
    }
    setImageFile(null);
    setImagePreview(null);
    setImageRemoved(false);
    setIsEditing(false);
  };

  const handleTogglePopup = (checked: boolean) => {
    if (checked && (config?.targetVersions || []).length === 0) {
      toast({
        title: 'Select target version first',
        description: 'Popup will stay off until at least one target version is selected.',
        status: 'warning',
      });
      return;
    }
    const optimistic = { ...config!, showPopup: checked };
    setConfig(optimistic);
    setForm((f) => ({ ...f, showPopup: checked }));
    const payload = new FormData();
    payload.append('showPopup', String(checked));
    togglePopupRequest({
      command: ApiCommand.PUT,
      url: urlConstants.appVersion.update,
      options: payload,
      callback: (data, error) => {
        if (error) {
          setConfig({ ...optimistic, showPopup: !checked });
          setForm((f) => ({ ...f, showPopup: !checked }));
          toast({ title: 'Failed to update', description: error, status: 'error' });
        } else {
          toast({ title: checked ? 'Popup enabled' : 'Popup disabled', status: 'success', duration: 2000 });
        }
      },
    });
  };

  const addTargetVersion = () => {
    if (!selectedDropdown) return;
    if (form.targetVersions.includes(selectedDropdown)) return;
    setForm((f) => ({ ...f, targetVersions: [...f.targetVersions, selectedDropdown] }));
    setSelectedDropdown('');
  };

  const removeTargetVersion = (v: string) => {
    setForm((f) => ({ ...f, targetVersions: f.targetVersions.filter((x) => x !== v) }));
  };

  const toggleTargetVersion = (v: string, checked: boolean) => {
    setForm((f) => ({
      ...f,
      targetVersions: checked
        ? [...f.targetVersions, v]
        : f.targetVersions.filter((x) => x !== v),
    }));
  };

  const addPublishedVersion = () => {
    const value = newPublishedVersion.trim();
    if (!value || form.publishedVersions.includes(value)) return;
    setForm((f) => ({ ...f, publishedVersions: [...f.publishedVersions, value] }));
    setNewPublishedVersion('');
  };

  const removePublishedVersion = (v: string) => {
    setForm((f) => ({
      ...f,
      publishedVersions: f.publishedVersions.filter((x) => x !== v),
      targetVersions: f.targetVersions.filter((x) => x !== v),
    }));
  };

  // Versions available to add (not yet selected)
  const availableVersions = form.publishedVersions.filter((v) => !form.targetVersions.includes(v));
  const visibleImageUrl = imageRemoved ? undefined : config?.imageUrl;

  return (
    <Page>
      <Flex justify='space-between' align='center' mb={6}>
        <Heading size='lg' fontWeight='bold'>Version Management</Heading>
        {!isEditing && (
          <Button leftIcon={<MdEdit />} colorScheme='primary' onClick={() => setIsEditing(true)}>
            Update Version
          </Button>
        )}
      </Flex>

      {isFetching ? (
        <Flex justify='center' align='center' minH='300px'>
          <Spinner size='xl' color='primary.500' />
        </Flex>
      ) : (
        <VStack spacing={5} align='stretch'>
          {/* General Information */}
          <Box bg='white' borderRadius='lg' p={6} boxShadow='sm' border='1px solid' borderColor='gray.100'>
            <Text fontWeight='bold' fontSize='md' mb={5}>General Information</Text>

            {isEditing ? (
              <VStack spacing={4} align='stretch'>
                <FormControl>
                  <FormLabel fontSize='sm' color='gray.600'>Update Title</FormLabel>
                  <StrongInput
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder='e.g. New version available'
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize='sm' color='gray.600'>Update Message</FormLabel>
                  <StrongTextarea
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder='Describe what changed in this version...'
                    rows={3}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize='sm' color='gray.600'>Update Image</FormLabel>
                  <input
                    type='file'
                    accept='image/*'
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleImageSelect}
                  />
                  {imagePreview || visibleImageUrl ? (
                    <Box position='relative' display='inline-block'>
                      <Image
                        src={imagePreview ?? visibleImageUrl}
                        alt='Update image'
                        borderRadius='md'
                        maxH='180px'
                        objectFit='cover'
                        border='1px solid'
                        borderColor='gray.200'
                      />
                      <IconButton
                        aria-label='Remove image'
                        icon={<MdClose />}
                        size='xs'
                        colorScheme='red'
                        position='absolute'
                        top={1}
                        right={1}
                        onClick={handleRemoveImage}
                      />
                    </Box>
                  ) : null}
                  <Button
                    mt={imagePreview || visibleImageUrl ? 2 : 0}
                    size='sm'
                    leftIcon={<MdUpload />}
                    variant='outline'
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {imagePreview || visibleImageUrl ? 'Replace Image' : 'Upload Image'}
                  </Button>
                </FormControl>

                {/* Target Versions — edit mode */}
                <Divider />
                <Box>
                  <Text fontWeight='medium' mb={1}>Published Versions</Text>
                  <Text fontSize='sm' color='gray.500' mb={3}>
                    Add real App Store / Play Store versions here. These are used for popup targeting.
                  </Text>

                  <Flex gap={2} mb={3}>
                    <StrongInput
                      value={newPublishedVersion}
                      onChange={(e) => setNewPublishedVersion(e.target.value)}
                      placeholder='e.g. 1.0.5'
                    />
                    <Button
                      leftIcon={<MdAdd />}
                      colorScheme='primary'
                      onClick={addPublishedVersion}
                      isDisabled={!newPublishedVersion.trim()}
                    >
                      Add
                    </Button>
                  </Flex>

                  {form.publishedVersions.length > 0 ? (
                    <Wrap mb={5}>
                      {form.publishedVersions.map((v) => (
                        <WrapItem key={v}>
                          <Tag size='md' colorScheme='green' borderRadius='full'>
                            <TagLabel>{v}</TagLabel>
                            <TagCloseButton onClick={() => removePublishedVersion(v)} />
                          </Tag>
                        </WrapItem>
                      ))}
                    </Wrap>
                  ) : (
                    <Text fontSize='sm' color='gray.400' mb={5}>No published versions added.</Text>
                  )}

                  <Text fontWeight='medium' mb={1}>Target Versions</Text>
                  <Text fontSize='sm' color='gray.500' mb={3}>
                    Select which app versions should see the popup. Users on these versions will be notified.
                  </Text>

                  {/* Dropdown + Add button */}
                  <Flex gap={2} mb={4}>
                    <Select
                      placeholder='Select a version to add...'
                      value={selectedDropdown}
                      onChange={(e) => setSelectedDropdown(e.target.value)}
                      maxW='240px'
                      size='sm'
                      borderRadius='md'
                    >
                      {availableVersions.map((v) => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </Select>
                    <Button
                      size='sm'
                      leftIcon={<MdAdd />}
                      colorScheme='primary'
                      onClick={addTargetVersion}
                      isDisabled={!selectedDropdown}
                    >
                      Add
                    </Button>
                  </Flex>

                  {/* All published versions as checkboxes */}
                  {form.publishedVersions.length > 0 ? (
                    <Box bg='gray.50' borderRadius='md' p={4} border='1px solid' borderColor='gray.200'>
                      <Text fontSize='xs' color='gray.500' mb={3} textTransform='uppercase'>
                        Published versions ({form.publishedVersions.length})
                      </Text>
                      <VStack align='stretch' spacing={2}>
                        {form.publishedVersions.map((v) => (
                          <Flex key={v} align='center' justify='space-between' py={1}>
                            <Checkbox
                              isChecked={form.targetVersions.includes(v)}
                              onChange={(e) => toggleTargetVersion(v, e.target.checked)}
                              colorScheme='primary'
                            >
                              <Text fontSize='sm'>{v}</Text>
                            </Checkbox>
                            {form.targetVersions.includes(v) && (
                              <Badge colorScheme='primary' fontSize='xs'>Selected</Badge>
                            )}
                          </Flex>
                        ))}
                      </VStack>
                    </Box>
                  ) : (
                    <Box bg='gray.50' borderRadius='md' p={4} textAlign='center'>
                      <Text fontSize='sm' color='gray.400'>Add a published version first.</Text>
                    </Box>
                  )}

                  {/* Selected tags */}
                  {form.targetVersions.length > 0 && (
                    <Box mt={3}>
                      <Text fontSize='xs' color='gray.500' mb={2}>Selected versions:</Text>
                      <Wrap>
                        {form.targetVersions.map((v) => (
                          <WrapItem key={v}>
                            <Tag size='md' colorScheme='primary' borderRadius='full'>
                              <TagLabel>{v}</TagLabel>
                              <TagCloseButton onClick={() => removeTargetVersion(v)} />
                            </Tag>
                          </WrapItem>
                        ))}
                      </Wrap>
                    </Box>
                  )}
                </Box>
              </VStack>
            ) : (
              <VStack align='stretch' spacing={4}>
                <Box>
                  <Text fontSize='xs' color='gray.500' textTransform='uppercase' mb={1}>Update Title</Text>
                  <Text fontWeight='semibold'>{config?.title || '-'}</Text>
                </Box>
                <Box bg='blue.50' borderRadius='md' p={4}>
                  <Text fontSize='sm' color='blue.600' fontWeight='medium' mb={1}>Update Message</Text>
                  <Text fontSize='sm' color='blue.700'>{config?.message || '-'}</Text>
                </Box>
                {config?.imageUrl && (
                  <Box>
                    <Text fontSize='xs' color='gray.500' textTransform='uppercase' mb={2}>Update Image</Text>
                    <Image
                      src={config.imageUrl}
                      alt='Update image'
                      borderRadius='md'
                      maxH='180px'
                      objectFit='cover'
                      border='1px solid'
                      borderColor='gray.200'
                    />
                  </Box>
                )}

                {/* Target Versions — view mode */}
                <Divider />
                <Box>
                  <Text fontSize='xs' color='gray.500' textTransform='uppercase' mb={2}>
                    Published Versions ({(config?.publishedVersions || []).length})
                  </Text>
                  {(config?.publishedVersions || []).length > 0 ? (
                    <Wrap mb={4}>
                      {(config?.publishedVersions || []).map((v) => (
                        <WrapItem key={v}>
                          <Tag size='md' colorScheme='green' borderRadius='full'>
                            <TagLabel>{v}</TagLabel>
                          </Tag>
                        </WrapItem>
                      ))}
                    </Wrap>
                  ) : (
                    <Text fontSize='sm' color='gray.400' mb={4}>No published versions added.</Text>
                  )}

                  <Text fontSize='xs' color='gray.500' textTransform='uppercase' mb={2}>
                    Target Versions ({(config?.targetVersions || []).length})
                  </Text>
                  {(config?.targetVersions || []).length > 0 ? (
                    <Wrap>
                      {(config?.targetVersions || []).map((v) => (
                        <WrapItem key={v}>
                          <Tag size='md' colorScheme='primary' borderRadius='full'>
                            <TagLabel>{v}</TagLabel>
                          </Tag>
                        </WrapItem>
                      ))}
                    </Wrap>
                  ) : (
                    <Text fontSize='sm' color='gray.400'>No target versions selected — popup will not show.</Text>
                  )}
                </Box>
              </VStack>
            )}
          </Box>

          {/* Platform Cards */}
          <Flex gap={4}>
            {/* iOS Card */}
            <Box flex={1} bg='white' borderRadius='lg' p={6} boxShadow='sm' border='1px solid' borderColor='gray.100'>
              <Flex align='center' gap={3} mb={4}>
                <Flex
                  align='center' justify='center'
                  bg='gray.900' borderRadius='xl'
                  w='42px' h='42px' flexShrink={0}
                >
                  <MdPhoneIphone size={22} color='white' />
                </Flex>
                <Box>
                  <Text fontWeight='bold' fontSize='md'>iOS</Text>
                  <Text fontSize='xs' color='gray.500'>Apple App Store</Text>
                </Box>
              </Flex>
              {isEditing ? (
                <VStack spacing={3} align='stretch'>
                  <FormControl>
                    <FormLabel fontSize='sm' color='gray.600'>iOS Version</FormLabel>
                    <StrongInput
                      value={form.iosVersion ?? ''}
                      onChange={(e) => setForm({ ...form, iosVersion: e.target.value })}
                      placeholder='e.g. 1.2.7'
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize='sm' color='gray.600'>App Store Link</FormLabel>
                    <StrongInput
                      value={form.iosStoreUrl ?? ''}
                      onChange={(e) => setForm({ ...form, iosStoreUrl: e.target.value })}
                      placeholder='https://apps.apple.com/...'
                    />
                  </FormControl>
                </VStack>
              ) : (
                <VStack align='stretch' spacing={2}>
                  <Box>
                    <Text fontSize='xs' color='gray.500' mb={1}>Current Version</Text>
                    <Text fontWeight='bold' fontSize='2xl'>{config?.iosVersion || '-'}</Text>
                  </Box>
                  {config?.iosStoreUrl ? (
                    <Flex
                      as='a'
                      href={config.iosStoreUrl}
                      target='_blank'
                      rel='noopener noreferrer'
                      align='center'
                      gap={1}
                      color='blue.500'
                      fontSize='sm'
                      fontWeight='medium'
                      _hover={{ textDecoration: 'underline' }}
                    >
                      <MdOpenInNew size={14} />
                      App Store
                    </Flex>
                  ) : (
                    <Text fontSize='xs' color='gray.400'>No store link set</Text>
                  )}
                </VStack>
              )}
            </Box>

            {/* Android Card */}
            <Box flex={1} bg='white' borderRadius='lg' p={6} boxShadow='sm' border='1px solid' borderColor='gray.100'>
              <Flex align='center' gap={3} mb={4}>
                <Flex
                  align='center' justify='center'
                  bg='green.500' borderRadius='xl'
                  w='42px' h='42px' flexShrink={0}
                >
                  <MdPhoneAndroid size={22} color='white' />
                </Flex>
                <Box>
                  <Text fontWeight='bold' fontSize='md'>Android</Text>
                  <Text fontSize='xs' color='gray.500'>Google Play Store</Text>
                </Box>
              </Flex>
              {isEditing ? (
                <VStack spacing={3} align='stretch'>
                  <FormControl>
                    <FormLabel fontSize='sm' color='gray.600'>Android Version</FormLabel>
                    <StrongInput
                      value={form.androidVersion ?? ''}
                      onChange={(e) => setForm({ ...form, androidVersion: e.target.value })}
                      placeholder='e.g. 1.2.7'
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize='sm' color='gray.600'>Play Store Link</FormLabel>
                    <StrongInput
                      value={form.androidStoreUrl ?? ''}
                      onChange={(e) => setForm({ ...form, androidStoreUrl: e.target.value })}
                      placeholder='https://play.google.com/store/apps/...'
                    />
                  </FormControl>
                </VStack>
              ) : (
                <VStack align='stretch' spacing={2}>
                  <Box>
                    <Text fontSize='xs' color='gray.500' mb={1}>Current Version</Text>
                    <Text fontWeight='bold' fontSize='2xl'>{config?.androidVersion || '-'}</Text>
                  </Box>
                  {config?.androidStoreUrl ? (
                    <Flex
                      as='a'
                      href={config.androidStoreUrl}
                      target='_blank'
                      rel='noopener noreferrer'
                      align='center'
                      gap={1}
                      color='green.600'
                      fontSize='sm'
                      fontWeight='medium'
                      _hover={{ textDecoration: 'underline' }}
                    >
                      <MdOpenInNew size={14} />
                      Play Store
                    </Flex>
                  ) : (
                    <Text fontSize='xs' color='gray.400'>No store link set</Text>
                  )}
                </VStack>
              )}
            </Box>
          </Flex>

          {/* Version Details */}
          <Box bg='white' borderRadius='lg' p={6} boxShadow='sm' border='1px solid' borderColor='gray.100'>
            <Text fontWeight='bold' fontSize='md' mb={5}>Version Details</Text>

            {isEditing ? (
              <VStack spacing={4} align='stretch'>
                <FormControl maxW='200px'>
                  <FormLabel fontSize='sm' color='gray.600'>Version Number</FormLabel>
                  <StrongInput
                    value={form.version}
                    onChange={(e) => setForm({ ...form, version: e.target.value })}
                    placeholder='e.g. 1.2.7'
                  />
                </FormControl>
                <Divider />
                <Flex justify='space-between' align='center'>
                  <Box>
                    <Text fontWeight='medium'>Force Update</Text>
                    <Text fontSize='sm' color='gray.500'>Require users to update before continuing</Text>
                  </Box>
                  <Switch
                    colorScheme='primary'
                    isChecked={form.forceUpdate}
                    onChange={(e) => setForm({ ...form, forceUpdate: e.target.checked })}
                    size='lg'
                  />
                </Flex>
                <Divider />
                <Flex justify='space-between' align='center'>
                  <Box>
                    <Text fontWeight='medium'>Show Popup</Text>
                    <Text fontSize='sm' color='gray.500'>Show update notification popup in the app</Text>
                  </Box>
                  <Switch
                    colorScheme='primary'
                    isChecked={form.showPopup}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      if (checked && form.targetVersions.length === 0) {
                        toast({
                          title: 'Select target version first',
                          description: 'Popup will stay off until at least one target version is selected.',
                          status: 'warning',
                        });
                        return;
                      }
                      setForm({ ...form, showPopup: checked });
                    }}
                    isDisabled={form.targetVersions.length === 0}
                    size='lg'
                  />
                </Flex>
              </VStack>
            ) : (
              <VStack align='stretch' spacing={0} divider={<Divider />}>
                <Flex justify='space-between' align='center' py={3}>
                  <Text color='gray.600'>Version</Text>
                  <Text fontWeight='bold' fontSize='lg'>{config?.version || '-'}</Text>
                </Flex>
                <Flex justify='space-between' align='center' py={3}>
                  <Text color='gray.600'>Force Update</Text>
                  <Badge colorScheme={config?.forceUpdate ? 'red' : 'green'} borderRadius='full' px={3} py={1} fontSize='sm'>
                    {config?.forceUpdate ? 'Required' : 'Optional'}
                  </Badge>
                </Flex>
                <Flex justify='space-between' align='center' py={3}>
                  <Box>
                    <Text color='gray.600'>Show Popup</Text>
                    <Text fontSize='xs' color='gray.400'>Toggle without entering edit mode</Text>
                  </Box>
                  <Flex align='center' gap={2}>
                    <Text fontSize='sm' color={config?.showPopup ? 'blue.500' : 'gray.400'} fontWeight='medium'>
                      {config?.showPopup ? 'Enabled' : 'Disabled'}
                    </Text>
                    <Switch
                      colorScheme='blue'
                      isChecked={config?.showPopup ?? false}
                      onChange={(e) => handleTogglePopup(e.target.checked)}
                      isDisabled={isTogglingPopup || (config?.targetVersions || []).length === 0}
                      size='lg'
                    />
                  </Flex>
                </Flex>
                <Flex justify='space-between' align='center' py={3}>
                  <Text color='gray.600'>Last Updated</Text>
                  <Text color='gray.500' fontSize='sm'>{formatDate(config?.updatedAt)}</Text>
                </Flex>
              </VStack>
            )}
          </Box>

          {/* Save / Cancel */}
          {isEditing && (
            <Flex gap={3} justify='flex-end'>
              <Button variant='outline' onClick={handleCancel} isDisabled={isSaving}>Cancel</Button>
              <Button colorScheme='primary' onClick={handleSave} isLoading={isSaving}>Save Changes</Button>
            </Flex>
          )}
        </VStack>
      )}
    </Page>
  );
};

export default NewVersion;
